import React from "react";
import { InlineMath } from "react-katex";

import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import {
  sampleAnchorCurve,
} from "./helpers/smoothGraph";

import {
  randInt,
  parseSingleInterval,
  getIntervalRaw,
  intervalsEqualList,
} from "../../temat6_zadania_uzupelniajace_maturalne/podtemat3_otwarte_types/helpers/MaturaPiecewiseFunctionUtils";

/*
  ReadMonotonicityFromGraphTask
  – odczyt przedziału monotoniczności z wykresu
  – łamana CIĄGŁA (2 odcinki, jedno załamanie)
  – różne zachowania na odcinkach (rosnąca / malejąca / stała)
  – pytamy o JEDEN typ, który rzeczywiście występuje
  – odpowiedź: SAM PRZEDZIAŁ (bez prefiksów)
*/

function pickClosedFlag() {
  return randInt(0, 1) === 1;
}

function lerp(xa, ya, xb, yb, x) {
  return ya + ((yb - ya) * (x - xa)) / (xb - xa);
}

function kindLabel(kind) {
  if (kind === "inc") return "rosnąca";
  if (kind === "dec") return "malejąca";
  return "stała";
}

function buildData() {
  // kombinacje zachowań na dwóch odcinkach
  const combos = [
    ["inc", "dec"],
    ["dec", "inc"],
    ["inc", "const"],
    ["const", "inc"],
    ["dec", "const"],
    ["const", "dec"],
  ];

  const [leftKind, rightKind] = combos[randInt(0, combos.length - 1)];

  const xL = randInt(-5, -3);
  const xR = randInt(3, 5);
  const h = randInt(-1, 1);

  const leftClosed = pickClosedFlag();
  const rightClosed = pickClosedFlag();

  let yL, yB, yR;

  yB = randInt(-3, 3);

  // lewy odcinek
  if (leftKind === "inc") {
    yL = randInt(-5, yB - 1);
  } else if (leftKind === "dec") {
    yL = randInt(yB + 1, 5);
  } else {
    yL = yB;
  }

  // prawy odcinek
  if (rightKind === "inc") {
    yR = randInt(yB + 1, 5);
  } else if (rightKind === "dec") {
    yR = randInt(-5, yB - 1);
  } else {
    yR = yB;
  }

  const eps = 0;

  const points = {
    A: {
      x: xL,
      y: yL,
      markerSymbol: leftClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
    AL: {
      x: xL + eps,
      y: lerp(xL, yL, h, yB, xL + eps),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },

    B: {
      x: h,
      y: yB,
      showDot: false,
      showMarker: false,
      showLabel: false,
    },

    CR: {
      x: xR - eps,
      y: lerp(h, yB, xR, yR, xR - eps),
      showDot: false,
      showMarker: false,
      showLabel: false,
      label: "",
    },
    C: {
      x: xR,
      y: yR,
      markerSymbol: rightClosed ? "circle-filled" : "circle-open",
      showLabel: false,
    },
  };

  const presentKinds = Array.from(new Set([leftKind, rightKind]));
  const askedKind = presentKinds[randInt(0, presentKinds.length - 1)];

  let answerInterval;
  if (askedKind === leftKind) {
    answerInterval = {
      left: xL,
      right: h,
      leftClosed,
      rightClosed: true, // punkt załamania należy do wykresu
    };
  } else {
    answerInterval = {
      left: h,
      right: xR,
      leftClosed: true,
      rightClosed,
    };
  }

  return {
    points,
    segments: [
      { p1: "AL", p2: "B" },
      { p1: "B", p2: "CR" },
    ],
    xRange: [-6, 6],
    yRange: [-6, 6],
    askedKind,
    answerInterval,
  };
}

function buildSmoothData() {
  const combos = [
    ["inc", "dec"],
    ["dec", "inc"],
    ["inc", "const"],
    ["const", "inc"],
    ["dec", "const"],
    ["const", "dec"],
  ];
  const [leftKind, rightKind] = combos[randInt(0, combos.length - 1)];
  const xL = randInt(-5, -3);
  const xR = randInt(3, 5);
  const h = randInt(-1, 1);
  const leftClosed = pickClosedFlag();
  const rightClosed = pickClosedFlag();
  const yB = randInt(-2, 2);

  const yL =
    leftKind === "inc"
      ? randInt(-5, yB - 1)
      : leftKind === "dec"
      ? randInt(yB + 1, 5)
      : yB;
  const yR =
    rightKind === "inc"
      ? randInt(yB + 1, 5)
      : rightKind === "dec"
      ? randInt(-5, yB - 1)
      : yB;

  const presentKinds = Array.from(new Set([leftKind, rightKind]));
  const askedKind = presentKinds[randInt(0, presentKinds.length - 1)];
  const answerInterval =
    askedKind === leftKind
      ? {
          left: xL,
          right: h,
          leftClosed,
          rightClosed: true,
        }
      : {
          left: h,
          right: xR,
          leftClosed: true,
          rightClosed,
        };

  const anchors = [
    { x: xL, y: yL },
    { x: h, y: yB },
    { x: xR, y: yR },
  ];

  return {
    points: {
      A: {
        ...anchors[0],
        markerSymbol: leftClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      C: {
        ...anchors[2],
        markerSymbol: rightClosed ? "circle-filled" : "circle-open",
        showLabel: false,
      },
      B: {
        ...anchors[1],
        showDot: false,
        showMarker: false,
        showLabel: false,
      },
    },
    curves: [
      sampleAnchorCurve(anchors, {
        style: "line",
        openStart: !leftClosed,
        openEnd: !rightClosed,
      }),
    ],
    xRange: [-6, 6],
    yRange: [-6, 6],
    askedKind,
    answerInterval,
  };
}

/* ===================== API ===================== */

export function generateTask() {
  return {
    type: "ReadMonotonicityFromGraphTask",
    data: buildSmoothData(),
  };
}

export function renderTask({ data }) {
  const sizePx = 330;

  return (
    <div className="flex flex-col gap-3">
      <p>
        Na wykresie przedstawiono funkcję. Podaj przedział, na którym funkcja jest{" "}
        <strong>{kindLabel(data.askedKind)}</strong>.
      </p>

      <div style={{ margin: "0 auto", maxWidth: sizePx }}>
        <GraphViewer
          width={sizePx}
          points={data.points}
          curves={data.curves}
          xRange={data.xRange}
          yRange={data.yRange}
        />
      </div>

      <div>
        Szukany przedział zapisz w postaci{" "}
        <InlineMath math={"x\\in\\_\\_\\_\\_"} />.
      </div>
    </div>
  );
}

export function renderInput(_task, userInput = {}, onChange, disabled) {
  const value = userInput.I ?? "";

  const handleChange = (val) => {
    onChange({
      ...(userInput || {}),
      I: val,
    });
  };

  const raw = getIntervalRaw(userInput, "I");
  const hasRaw = typeof raw === "string" && raw.trim().length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <strong>Przedział:</strong>
      </div>
      <div><InlineMath math={"x\\in"} /></div>
      <div>
        <IntervalInput
          value={value}
          onChange={handleChange}
          allowUnion={false}
          disabled={disabled}
          showPreview={false}
        />
      </div>

      {hasRaw ? (
        <div>
          <InlineMath math={`x\\in ${raw}`} />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer(task, userInput = {}) {
  const expected = task?.data?.answerInterval;
  if (!expected) return false;

  const raw = getIntervalRaw(userInput, "I");
  const parsed = parseSingleInterval(raw);
  if (!parsed) return false;

  return intervalsEqualList(
    [
      {
        left: expected.left,
        right: expected.right,
        leftClosed: !!expected.leftClosed,
        rightClosed: !!expected.rightClosed,
      },
    ],
    [
      {
        left: parsed.left,
        right: parsed.right,
        leftClosed: !!parsed.leftClosed,
        rightClosed: !!parsed.rightClosed,
      },
    ]
  );
}

export function renderAnswer(task) {
  const I = task?.data?.answerInterval;
  if (!I) return null;

  const l = I.leftClosed ? "[" : "(";
  const r = I.rightClosed ? "]" : ")";
  return <InlineMath math={`x\\in ${l}${I.left},\\,${I.right}${r}`} />;
}

export function renderExplanation(task) {
  const kind = task?.data?.askedKind;
  const I = task?.data?.answerInterval;
  if (!kind || !I) return null;

  const l = I.leftClosed ? "[" : "(";
  const r = I.rightClosed ? "]" : ")";
  const latex = `x\\in ${l}${I.left},\\,${I.right}${r}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Sprawdź, na którym fragmencie wykres jest{" "}
        <strong>{kindLabel(kind)}</strong>.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Odczytaj z osi <InlineMath math={"x"} /> przedział,
        dla którego ten fragment wykresu występuje.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Uwaga.</strong> W punkcie załamania funkcja istnieje, więc przedział
        jest tam <strong>domknięty</strong>. Końce przedziału zależą od kółek
        otwartych lub domkniętych na wykresie.
      </div>

      <div style={{ marginTop: 10 }}>
        <InlineMath math={latex} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = getIntervalRaw(userAnswer, "I");
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`x\\in ${raw}`} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
